import { fetcher } from '../../../lib/api-fetcher';
import { useMemo, useState, useCallback } from 'react';
import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../lib/hooks';
import { getSession } from '../../../lib/session';
import { useSchemes } from '../../../features/schemes/network/schemes.api';
import type { SuretyRequestItem, SuretyRequestStatus } from '../../../features/surety/types';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Eye, UserPlus } from 'lucide-react';


const STATUS_CONFIG: Record<SuretyRequestStatus, { label: string; variant: 'warning' | 'info' | 'success' | 'danger' }> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  PARTIAL: { label: 'Partial Approval', variant: 'info' },
  BOTH_APPROVED: { label: 'Both Approved', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
};

import { PageHeader } from '../../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/surety/')({
  component: SuretyRequestsPage,
});

function SuretyRequestsPage() {
  usePageTitle('Surety Requests');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const session = getSession();

  const suretyConfig = useMemo(
    () =>
      defineListPage<SuretyRequestItem>({
        variant: 'data-table',
        resource: 'surety-requests',
        columns: [
          {
            id: 'winnerSubscriberId',
            header: 'Subscriber (Winner)',
            accessor: (row) => row.winnerSubscriberId.slice(0, 8) + '…',
            renderType: 'code',
          },
          {
            id: 'schemeId',
            header: 'Scheme',
            accessor: 'schemeId',
          },
          {
            id: 'deadline',
            header: 'Deadline',
            accessor: 'deadline',
            renderType: 'date',
          },
          {
            id: 'status',
            header: 'Status',
            accessor: 'status',
          },
          {
            id: 'createdAt',
            header: 'Created At',
            accessor: 'createdAt',
            renderType: 'date',
          },
        ],
        rowActions: [
          {
            label: 'View Request Details',
            icon: <Eye className="w-4 h-4" />,
            onClick: (row) => navigate({ to: '/surety/$id', params: { id: row.id } }),
          },
          {
            label: 'Add Nominee / Guarantor',
            icon: <UserPlus className="w-4 h-4" />,
            hidden: (row) => row.status === 'BOTH_APPROVED' || row.status === 'REJECTED',
            onClick: (row) => navigate({ to: '/surety/$id/nominees/new', params: { id: row.id } }),
          },
        ],
        filters: [
          {
            id: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { label: 'Pending', value: 'PENDING' },
              { label: 'Partial Approval', value: 'PARTIAL' },
              { label: 'Both Approved', value: 'BOTH_APPROVED' },
              { label: 'Rejected', value: 'REJECTED' },
            ],
          },
        ],
        defaultPageSize: 20,
      }),
    [navigate],
  );
  const canRead =
    !session ||
    session?.user?.type === 'staff' ||
    session?.user?.type === 'company_admin' ||
    session?.user?.type === 'group_admin' ||
    (session?.permissions?.includes('chitfunds.surety.read') ?? true);


  const [schemeId, setSchemeId] = useState<string>('');
  const { data: schemesResponse } = useSchemes({ limit: 100 });
  const schemesList = schemesResponse?.data || [];

  const fetchSurety: PaginatedQueryFn<SuretyRequestItem> = useCallback(async (params) => {
    const qs = new URLSearchParams();
    if (schemeId) qs.append('schemeId', schemeId);
    const all = await fetcher.get<any>(`/staff/v1/surety-requests?${qs}`);
    const items = (all?.data || all || []) as SuretyRequestItem[];

    const status = params.status;
    const filtered = status ? items.filter((r) => r.status === status) : items;

    const page = parseInt(params.page || '1', 10) || 1;
    const limit = parseInt(params.limit || '20', 10) || 20;
    const start = (page - 1) * limit;
    return {
      data: filtered.slice(start, start + limit),
      meta: { total: filtered.length, page, limit },
    };
  }, [schemeId]);

  const urlAdapter = useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const list = useListPage(suretyConfig, fetchSurety, urlAdapter);

  if (!canRead) {
    return (
      <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
        <PageHeader title="Surety Requests" subtitle="Verify guarantors, process nominee profiles, and clear prize payout blocks." />
        <div className="flex items-center justify-center h-48 bg-surface-1 border border-surface-3 rounded-sm">
          <p className="text-sm text-text-secondary">You do not have permission to view Surety Requests.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Surety Requests"
        subtitle="Verify guarantors, process nominee profiles, and clear prize payout blocks."
      />

      <DataTable
        config={suretyConfig}
        list={list}
        toolbarActions={
          <select
            value={schemeId}
            onChange={(e) => setSchemeId(e.target.value)}
            className="h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 font-semibold"
          >
            <option value="">All Schemes</option>
            {schemesList.map((s) => (
              <option key={s.id} value={s.id}>{s.name || s.schemeNumber}</option>
            ))}
          </select>
        }
        customRenderers={{
          winnerSubscriberId: (row) => (
            <Link
              to="/surety/$id"
              params={{ id: row.id }}
              className="font-mono font-medium text-accent-500 hover:underline"
            >
              {row.winnerSubscriberId.slice(0, 8)}…
            </Link>
          ),
          status: (row) => {
            const cfg = STATUS_CONFIG[row.status as SuretyRequestStatus] ?? { label: row.status, variant: 'info' as const };
            return <StatusBadge status={cfg.variant} label={cfg.label} />;
          },
          schemeId: (row) => {
            const scheme = schemesList.find((s) => s.id === row.schemeId);
            return <span className="font-medium text-text-primary">{scheme?.name || scheme?.schemeNumber || row.schemeId}</span>;
          },
        }}
      />
    </div>
  );
}
